/**
 * SPEC.md §10, §11 — M6.1 (issue #28). The `?` cheatsheet, rendered
 * straight from `shortcuts.ts`'s `SHORTCUTS` list so it can never drift
 * out of sync with what's actually bound.
 *
 * A backdrop-modal, not a topbar dropdown — `useMenu` (M5.1) assumes a
 * trigger button driving `toggle()`, but this opens from a global `?`
 * keydown instead, so App.tsx just owns a plain boolean and this
 * component shares `useMenu`'s own `useEscapeKey` for the close behavior.
 */
import { formatShortcut, SHORTCUTS } from '../lib/shortcuts';
import { useEscapeKey } from './useEscapeKey';

interface ShortcutCheatsheetProps {
  onClose: () => void;
}

export default function ShortcutCheatsheet({ onClose }: ShortcutCheatsheetProps) {
  useEscapeKey(onClose, true);

  return (
    <div
      // ink at high opacity, not Tailwind's bare `black` — CLAUDE.md rule
      // 10: every color here stays backed by a --color-* token, including
      // the backdrop.
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts"
        onClick={(e) => e.stopPropagation()}
        className="w-80 rounded-sm border border-ink-3 bg-ink-2 p-4 shadow-lg"
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="pane-label text-mute">Keyboard shortcuts</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-sm px-2 py-1 text-xs text-mute hover:bg-ink-3 hover:text-text-ink"
          >
            Esc
          </button>
        </div>
        <ul className="flex flex-col gap-2">
          {SHORTCUTS.map((shortcut) => (
            <li key={shortcut.id} className="flex items-center justify-between text-xs text-text-ink">
              <span>{shortcut.description}</span>
              <kbd className="rounded-sm border border-ink-3 bg-ink px-1.5 py-0.5 font-mono text-[11px]">
                {formatShortcut(shortcut.keys)}
              </kbd>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
